import React, { useCallback, useEffect, useState } from "react";
import "./GamePlayer.scss";
import background from "../../../assets/img/game-landscape-lab.png";
import level1 from "../../../assets/gamedata/level1.json";
import level2 from "../../../assets/gamedata/level2.json";
import level3 from "../../../assets/gamedata/level3.json";
import level4 from "../../../assets/gamedata/level4.json";
import level5 from "../../../assets/gamedata/level5.json";
import { GameTabs } from "./GameTabs/GameTabs";
import { DNAAnswers, DNAElementValue, DNAValue } from "../../../components/Game/DNA/DNA";
import { clone } from "lodash";
import { GameSplash } from "./GameSplash/GameSplash";
import { GameWelcome } from "./GameWelcome/GameWelcome";
import { GameStateContext } from "../../../context/GameStateContext";
import { Levels } from "./Levels/Levels";
import dayjs from "dayjs";
import { saveHighscore } from "../../../utils/highScores";
import { Tutorial } from "../../../components/Game/Tutorial/Tutorial";
import { Doors } from "../../../components/Game/Doors/Doors";

export type TLevel = 1 | 2 | 3 | 4 | 5;
export type GameState = 'splash' |
    'welcome' |
    'intro' |
    'tutorial' |
    'playing' |
    'level-tutorial' |
    'level-doors' |
    'game-over' |
    'win';
export interface LevelGameData {
    result: DNAValue
    answers: DNAAnswers,
    wrongAnswers: DNAElementValue[],
}
export interface FullGameState {
    playerId: number
    points: number
    level: TLevel
    alert: 'none' | 'red' | 'blue'
    state: GameState
}

interface IProps {

}

/**
 *
 *
 * @param props
 * @constructor
 */
export function GamePlayer(props: IProps) {
    const [renderGame, setRenderGame] = useState(true);
    const [fullGameState, setFullGameState] = useState<FullGameState>({
        playerId: dayjs().unix(),
        points: 0,
        level: 1,
        alert: 'none',
        state: 'splash',
    });
    const {
        playerId,
        level,
        points,
        alert,
        state: gameState,
    } = fullGameState;

    const setPoints = ((value: number | ((value: number) => number)) => {
        setFullGameState((prevState) => {
            let points = value;
            if (typeof value === 'function') {
                points = value(prevState.points);
            }
            return {
                ...prevState,
                points: points as number,
            }
        });
    });

    const setGameState = ((value: GameState | ((value: GameState) => GameState)) => {
        setFullGameState((prevState) => {
            let state = value;
            if (typeof value === 'function') {
                state = value(prevState.state);
            }
            return {
                ...prevState,
                state: state as GameState,
            }
        });
    });

    const setAlert = ((value: ('none' | 'blue' | 'red') | ((value:  'none' | 'blue' | 'red') =>  'none' | 'blue' | 'red')) => {
        setFullGameState((prevState) => {
            let alert = value;
            if (typeof value === 'function') {
                alert = value(prevState.alert);
            }
            return {
                ...prevState,
                alert: alert as 'none' | 'blue' | 'red',
            }
        });
    });

    const gameData: LevelGameData[] = [
        level1,
        level2,
        level3,
        level4,
        level5,
    ] as LevelGameData[];

    const addPoints = (points: number) => {
        setPoints((prevState) => {
            return clone(prevState) + points;
        });
    }

    const handleFinish = (level: number) => {
        return () => {
            if (level === gameData.length) {
                saveHighscore(playerId, points, true);
                setFullGameState((prevState) => (
                    {
                        ...prevState,
                        alert: 'blue',
                        state: 'win',
                    }
                ))
            } else {
                setFullGameState((prevState) => (
                    {
                        ...prevState,
                        level: (clone(prevState.level) + 1 as TLevel),
                        state: 'level-doors',
                    }
                ));
            }
        }
    }

    // Show the tutorial for level 2 / 3
    useEffect(() => {
        if (level === 2 || level === 3) {
            setGameState('level-tutorial');
        }
    }, [level])

    const resetGame = useCallback((action: 'init' | 'play-again') => {
        setFullGameState((prevState) => ({
            ...prevState,
            alert: action === 'init' ? 'none' : 'red',
            state: action === 'init' ? 'splash' : 'level-doors',
            points: 0,
            level: 1,
            playerId: dayjs().unix(),
        }));
        setRenderGame(false);
        setTimeout(() => {
            setRenderGame(true);
        }, 1);
    }, []);

    // Reset game after a minute of inactivity
    useEffect(() => {
        if (gameState === 'playing' || gameState === 'splash') return;
        const timeUntilResetInSeconds = 60;
        const fullReset = () => {
            resetGame('init');
        }
        let timeout = setTimeout(fullReset, timeUntilResetInSeconds * 1000);
        const resetTimeoutOnTouch = () => {
            clearTimeout(timeout);
            timeout = setTimeout(fullReset, timeUntilResetInSeconds * 1000);
        }
        window.addEventListener('pointerdown', resetTimeoutOnTouch);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('pointerdown', resetTimeoutOnTouch);
        }
    }, [level, gameState, resetGame]);

    // Rerender the game on win or end
    useEffect(() => {
        if (gameState === 'win' || gameState === 'game-over') {
            setRenderGame(false);
            setTimeout(() => {
                setRenderGame(true);
            }, 1);
        }
    }, [gameState])

    const showGame = ['playing', 'level-tutorial', 'level-doors'].includes(gameState);
    const doorsClosed = gameState === 'level-doors' || gameState === 'level-tutorial';

    return (
        <GameStateContext.Provider value={{
            state: gameState,
            setState: setGameState,
            points,
            addPoints,
            level,
            playerId,
            resetGame
        }}>
            <div className={`game-player-wrapper alert-${alert}`} style={{backgroundImage: `url(${background})`}}>
                {renderGame ? (<>
                    <GameSplash visible={gameState === 'splash'} setGameState={setGameState} />
                    <GameWelcome
                        setRedAlert={() => setAlert('red')}
                    />
                    <GameTabs gameState={gameState} setGameState={setGameState} />



                    <div className={`actual-game level-${level} ${showGame ? 'visible' : ''}`}>
                        <Levels
                            gameData={gameData}
                            onFinish={handleFinish}
                            currentLevel={level}
                        />
                    </div>

                    <Doors state={doorsClosed ? 'closed' : 'open'} visible={showGame} />

                    <Tutorial />
                </>) : <GameTabs gameState={gameState} setGameState={setGameState} />}

            </div>
        </GameStateContext.Provider>
    )
}
